import React from 'react';

import { PrismicRichText } from 'types/prismicRichtext';
import { PrismicVideoEmbed } from 'types/prismicVideo';
import { Video } from 'components/video/Video';
import { RichText } from 'components/rich-text/RichText';
import { IPrismicVimeoVideoEmbed } from 'types/prismicVideo';

interface IVideoContainer {
  video: PrismicVideoEmbed;
  caption: PrismicRichText;
  credits: PrismicRichText;
  isColumnLayout?: boolean;
}

function parse(url: string) {
  // - Supported YouTube URL formats:
  //   - http://www.youtube.com/watch?v=My2FRPA3Gf8
  //   - http://youtu.be/My2FRPA3Gf8
  //   - https://youtube.googleapis.com/v/My2FRPA3Gf8
  // - Supported Vimeo URL formats:
  //   - http://vimeo.com/25451551
  //   - http://player.vimeo.com/video/25451551
  // - Also supports relative URLs:
  //   - //player.vimeo.com/video/25451551

  if (!url) {
    return undefined;
  }

  url.match(
    // tslint:disable-next-line:max-line-length
    /(http:|https:|)\/\/(player.|www.)?(vimeo\.com|youtu(be\.com|\.be|be\.googleapis\.com))\/(video\/|embed\/|watch\?v=|v\/)?([A-Za-z0-9._%-]*)(\&\S+)?/
  );

  let type;

  if (RegExp.$3.indexOf('youtu') > -1) {
    type = 'youtube';
  } else if (RegExp.$3.indexOf('vimeo') > -1) {
    type = 'vimeo';
  }

  return {
    type,
    id: RegExp.$6,
  };
}

function getSource(provider: string, id: number, embedUrl: string) {
  if (provider === 'Vimeo') {
    return `https://player.vimeo.com/video/${id}?autoplay=0&title=0&byline=0&portrait=0&quality=720p`;
  }

  if (provider === 'YouTube') {
    const parsed = parse(embedUrl);

    if (parsed) {
      return `https://www.youtube.com/embed/${parsed.id}?&autoplay=0`;
    }
  }

  return null;
}

export const VideoContainer = ({
  video,
  caption,
  credits,
  isColumnLayout,
}: IVideoContainer) => {
  const provider = video?.provider_name ?? null;
  const embedUrl = video?.embed_url ?? null;
  const id = parseInt((video as IPrismicVimeoVideoEmbed).video_id ?? '0', 10);

  if (!provider || !embedUrl) {
    return null;
  }

  const src = getSource(provider, id, embedUrl);

  if (!src) {
    return null;
  }

  return (
    <Video
      src={src}
      caption={caption ? <RichText>{caption}</RichText> : undefined}
      credits={credits ? <RichText>{credits}</RichText> : undefined}
      offset={!isColumnLayout}
    />
  );
};
